import { Close, FilterAltOutlined } from "@mui/icons-material";
import { Badge, Button, Drawer, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import React from "react";
import { useToggleView } from "../../hooks";
import { SelectBoxOption } from "../../interfaces";
import { Scope, useLazyGetTagGroupsQuery } from "../../redux/services";
import { EntityType } from "../boardLayout/types";
import { TagCategoriesDropdown, TagsHighlightSelect } from "../tagsDropdown";

export const BoardViewFilter: React.FC<{ entity: EntityType, scope: Scope, onFilterApply: (tags: SelectBoxOption[]) => void }> = ({ entity, scope, onFilterApply }) => {

    const [getTagGroups, tagGroupResponse] = useLazyGetTagGroupsQuery();

    const [selectedTags, setSelectedTags] = React.useState<{ [key: string]: SelectBoxOption[] }>({});
    const [tagsToFilter, setTagsToFilter] = React.useState<{ [key: string]: SelectBoxOption[] }>({});
    const [filterApplied, setFilterApplied] = React.useState<boolean>(false);
    const [open, setOpen] = React.useState(false);

    const { selectedBoard } = useToggleView(entity);

    const applyFilter = () => {
        const tags: SelectBoxOption[] = Object.values(selectedTags).flat();
        onFilterApply(tags);
        setFilterApplied(tags.length > 0);
        setTagsToFilter({ ...selectedTags });
        toggleDrawer(false)();
    };

    const toggleDrawer = (newOpen: boolean) => () => {
        if (newOpen) {
            setSelectedTags({ ...tagsToFilter });
        } else {
            setSelectedTags({});
        }
        setOpen(newOpen);
    };

    const fetchTagGroups = () => {
        getTagGroups({ scope: scope });
    };

    React.useEffect(() => {
        fetchTagGroups();
        // eslint-disable-next-line
    }, [scope]);
    
    React.useEffect(() => {
        setSelectedTags({});
        setFilterApplied(false);
        onFilterApply([]);
        setTagsToFilter({});
        // eslint-disable-next-line
    }, [selectedBoard?._id]);

    const isLoading = tagGroupResponse.isLoading || tagGroupResponse.isFetching;

    return <>
        <Tooltip title={isLoading ? `Fetching Tag Groups...` : `Advance Filter`} arrow>
            <Button
                disableRipple
                variant="outlined"
                color="warning"
                disabled={isLoading}
                sx={{ minWidth: "8px", backgroundColor: (theme) => theme.palette.background.paper }}
                onClick={toggleDrawer(true)}
            >
                <Badge
                    color="warning"
                    variant="dot"
                    invisible={!(filterApplied)}
                >
                    <FilterAltOutlined />
                </Badge>
            </Button>
        </Tooltip>

        <TagCategoriesDropdown entity={entity} tagGroupResponse={tagGroupResponse} refetchTagGroups={fetchTagGroups} />

        <Drawer open={open} onClose={toggleDrawer(false)} anchor="right">

            <Stack direction={"column"} spacing={2} sx={{ padding: 2, width: { xs: '90%', sm: 300 } }}>

                <Stack direction={"row"} justifyContent="space-between" alignItems={"center"}>
                    <Typography variant="h6" sx={{ fontFamily: "htrts_medium" }}>Filter</Typography>

                    <IconButton onClick={toggleDrawer(false)}>
                        <Close />
                    </IconButton>
                </Stack>


                {tagGroupResponse.data?.data?.filter(d => d?._id !== selectedBoard?._id)?.map((tagGroup) => (
                    <TagsHighlightSelect
                        key={tagGroup._id}
                        id={tagGroup._id}
                        canonical_name={tagGroup.canonical_name}
                        margin="none"
                        size="small"
                        multiple
                        value={selectedTags[tagGroup.canonical_name] ?? []}
                        onChange={(data: SelectBoxOption[]) => setSelectedTags({ ...selectedTags, [tagGroup.canonical_name]: data })}
                    />
                ))}

                <Stack direction={"row"} justifyContent="flex-end" alignItems={"center"} spacing={1}>
                    <Button variant="outlined" onClick={() => setSelectedTags({})}>Reset</Button>
                    <Button variant="contained" onClick={() => applyFilter()}>Apply</Button>
                </Stack>
            </Stack>
        </Drawer>
    </>
};